import { useState } from "react";
import { parseTitle, parseTitleAuthors, type AuthorsStatus } from "@/lib/titleAuthors";
import { testIds } from "@/testIds";
import { PaperPreview } from "./PaperPreview";
import styles from "./TitleEditContent.module.scss";

interface PreviewStripProps {
  title: string;
  author: string;
  status: AuthorsStatus;
  dateText: string;
  highlightSymbol: string | null;
  /** Width the paper takes when opened full-width. */
  width: number;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** "As it prints · 2 authors side by side · 2 notes" */
export function stripSummary(title: string, author: string): string {
  const titleNotes = parseTitle(title).notes.length;
  const authors = parseTitleAuthors(author, titleNotes);
  const notes = titleNotes + authors.reduce((n, a) => n + a.notes.length, 0);
  const parts = ["As it prints"];
  if (authors.length > 1) parts.push(`${authors.length} authors ${authors.length <= 3 ? "side by side" : "in rows"}`);
  else if (authors.length === 1) parts.push("1 author");
  if (notes > 0) parts.push(plural(notes, "note"));
  return parts.join(" · ");
}

/** Under 600 px the paper is a strip: a thumbnail, what it shows, and Show. */
export function PreviewStrip({ title, author, status, dateText, highlightSymbol, width }: PreviewStripProps) {
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.strip} data-testid={testIds.titleEdit.previewStrip} data-open={open}>
      <div className={styles.stripRow}>
        {!open && (
          <div className={styles.thumb} aria-hidden="true">
            <PaperPreview
              title={title}
              author={author}
              status={status}
              dateText={dateText}
              highlightSymbol={null}
              width={44}
              thumbnail
            />
          </div>
        )}
        <span className={styles.stripText}>{stripSummary(title, author)}</span>
        <button
          type="button"
          className={styles.stripShow}
          data-testid={testIds.titleEdit.previewShow}
          aria-expanded={open}
          onClick={() => setOpen(!open)}
        >
          {open ? "Hide" : "Show"}
        </button>
      </div>
      {open && (
        <PaperPreview
          title={title}
          author={author}
          status={status}
          dateText={dateText}
          highlightSymbol={highlightSymbol}
          width={width}
        />
      )}
    </div>
  );
}
